/**
 * Maps the statistics UI range keys ("week", "month", ...) to concrete,
 * browsable periods that follow the user's week/month start settings.
 *
 * The statistics service only works with explicit periods; this is the one
 * place that knows what "3 months" means, which granularity to chart it with
 * and how to move to the previous/next period.
 */

import {
  DatePeriod,
  PeriodSettings,
  accountingMonthAnchor,
  addAccountingMonths,
  endOfAccountingMonth,
  endOfDayLocal,
  resolvePeriodWithOffset,
  startOfAccountingMonth,
} from './date-period';

export type GranularityType = 'daily' | 'weekly' | 'monthly';

/** Range keys used by the statistics screens (persisted on the Home card). */
export type StatsRangeKey = 'week' | 'currentMonth' | 'month' | '3months' | '6months' | 'year';

export type StatsPeriodKind = 'week' | 'month' | 'rolling30' | 'months3' | 'months6' | 'year';

export interface StatsPeriodSpec {
  range: StatsRangeKey;
  kind: StatsPeriodKind;
  /** The selected period (inclusive, local time). */
  period: DatePeriod;
  /** The period of the same kind directly before `period` (for comparisons). */
  previousPeriod: DatePeriod;
  /** Bucket size for the spending chart. */
  granularity: GranularityType;
  /** Months of history for the monthly trend series. */
  trendMonths: number;
  /** True when the period extends past `now` (buckets after today are gaps). */
  markFuture: boolean;
}

/**
 * Resolves a UI range key to explicit periods. `offset` browses whole periods:
 * -1 = previous period of the same kind, 0 = current.
 */
export function resolveStatsPeriod(
  range: StatsRangeKey,
  settings: PeriodSettings,
  offset: number = 0,
  now: Date = new Date()
): StatsPeriodSpec {
  const build = (kind: StatsPeriodKind, period: DatePeriod, previousPeriod: DatePeriod,
    granularity: GranularityType, trendMonths: number): StatsPeriodSpec => ({
    range,
    kind,
    period,
    previousPeriod,
    granularity,
    trendMonths,
    // Only days after today count as future; today itself is still "current"
    markFuture: period.end.getTime() > endOfDayLocal(now).getTime(),
  });

  switch (range) {
    case 'week':
      return build('week',
        resolvePeriodWithOffset('thisWeek', settings, offset, now)!,
        resolvePeriodWithOffset('thisWeek', settings, offset - 1, now)!,
        'daily', 2);

    case 'currentMonth':
      return build('month',
        resolvePeriodWithOffset('thisMonth', settings, offset, now)!,
        resolvePeriodWithOffset('thisMonth', settings, offset - 1, now)!,
        'daily', 6);

    case 'month':
      // Rolling 30 days; browsing moves in 30-day steps
      return build('rolling30',
        resolvePeriodWithOffset('last30Days', settings, offset, now)!,
        resolvePeriodWithOffset('last30Days', settings, offset - 1, now)!,
        'daily', 6);

    case '3months':
      return build('months3',
        accountingMonthBlock(3, offset, settings, now),
        accountingMonthBlock(3, offset - 1, settings, now),
        'weekly', 6);

    case '6months':
      return build('months6',
        accountingMonthBlock(6, offset, settings, now),
        accountingMonthBlock(6, offset - 1, settings, now),
        'monthly', 6);

    case 'year':
      return build('year',
        resolvePeriodWithOffset('thisYear', settings, offset, now)!,
        resolvePeriodWithOffset('thisYear', settings, offset - 1, now)!,
        'monthly', 12);

    default: {
      const exhaustiveCheck: never = range;
      throw new Error(`Unknown statistics range: ${String(exhaustiveCheck)}`);
    }
  }
}

/**
 * A block of `count` whole accounting months ending with the current one
 * (offset 0); each offset step moves by a whole block.
 */
function accountingMonthBlock(count: number, offset: number, settings: PeriodSettings, now: Date): DatePeriod {
  const { monthStartDay } = settings;
  const currentStart = startOfAccountingMonth(now, monthStartDay);
  const lastMonthStart = addAccountingMonths(currentStart, offset * count);
  const start = addAccountingMonths(lastMonthStart, -(count - 1));
  const end = endOfAccountingMonth(lastMonthStart, monthStartDay);
  return { start, end };
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export type DateFormatter = (date: Date, options: Intl.DateTimeFormatOptions) => string;

export interface StatsPeriodLabels {
  /** Main label: "September 2026", "2026", "14 Sep – 20 Sep 2026", ... */
  title: string;
  /** Exact date range when the title alone is ambiguous (custom month start). */
  subtitle: string | null;
}

/** Human-readable labels for a resolved period; `formatDate` supplies the locale. */
export function formatStatsPeriodLabels(
  spec: StatsPeriodSpec,
  settings: PeriodSettings,
  formatDate: DateFormatter
): StatsPeriodLabels {
  const { monthStartDay } = settings;
  const { period } = spec;
  const range = formatRange(period, formatDate);
  const rangeIfShifted = monthStartDay !== 1 ? range : null;
  const monthYear = (date: Date) => capitalize(formatDate(date, { month: 'long', year: 'numeric' }));
  const shortMonthYear = (date: Date) => capitalize(formatDate(date, { month: 'short', year: 'numeric' }));

  switch (spec.kind) {
    case 'month':
      return { title: monthYear(accountingMonthAnchor(period.start, monthStartDay)), subtitle: rangeIfShifted };

    case 'year':
      return {
        title: String(accountingMonthAnchor(period.start, monthStartDay).getFullYear()),
        subtitle: rangeIfShifted,
      };

    case 'months3':
    case 'months6': {
      const firstAnchor = accountingMonthAnchor(period.start, monthStartDay);
      const lastAnchor = accountingMonthAnchor(startOfAccountingMonth(period.end, monthStartDay), monthStartDay);
      return { title: `${shortMonthYear(firstAnchor)} – ${shortMonthYear(lastAnchor)}`, subtitle: rangeIfShifted };
    }

    case 'week':
    case 'rolling30':
      return { title: range, subtitle: null };

    default: {
      const exhaustiveCheck: never = spec.kind;
      throw new Error(`Unknown statistics period kind: ${String(exhaustiveCheck)}`);
    }
  }
}

function formatRange(period: DatePeriod, formatDate: DateFormatter): string {
  const sameYear = period.start.getFullYear() === period.end.getFullYear();
  const startText = formatDate(period.start, sameYear
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
  const endText = formatDate(period.end, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startText} – ${endText}`;
}

function capitalize(text: string): string {
  return text.length > 0 ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : text;
}
